<div class="step-basic-info" *transloco="let t" data-testid="wizard-step-basic-info">
  <p-card>
    <ng-template #header>
      <div class="card-header">
        <i class="pi pi-home header-icon"></i>
        <h2 class="header-title">{{ t('objects.wizard.basicInfo.title') }}</h2>
      </div>
    </ng-template>

    <form [formGroup]="form" class="card-content">
      <app-wizard-step-hint
        icon="pi pi-images"
        [text]="t('positioning.wizard.basicInfo')" />

      <!-- Gallery Section -->
      <div class="form-group">
        <label class="group-label">{{ t('objects.wizard.basicInfo.gallery') }}</label>
        <app-gallery-upload
          data-testid="wizard-basic-gallery"
          [minImages]="limits.PROPERTY_MIN_IMAGES"
          [maxImages]="limits.PROPERTY_MAX_IMAGES"
          [disabled]="isViewMode()"
          [ngModel]="form.get('images')?.value"
          [ngModelOptions]="{ standalone: true }"
          (imagesChange)="onImagesChange($event)"
          (uploadStateChange)="onGalleryUploadState($event)"
        />
        <small class="field-hint">
          {{ t('objects.wizard.gallery.imageLimitHint', { current: form.get('images')?.value?.length || 0, max: limits.PROPERTY_MAX_IMAGES, globalCurrent: totalImages(), globalMax: limits.GLOBAL_MAX_IMAGES }) }}
        </small>
      </div>

      <!-- Name -->
      <div class="form-group">
        <label class="group-label">{{ t('objects.wizard.basicInfo.name') }}</label>
        <input
          pInputText
          id="name"
          formControlName="name"
          data-testid="wizard-basic-name"
          [fluid]="true"
          [placeholder]="t('objects.wizard.basicInfo.namePlaceholder')"
        />
        @if (getErrorMessage('name'); as error) {
          <small class="field-error">{{ error }}</small>
        }
      </div>

      <!-- Registration Number -->
      <div class="form-group">
        <label class="group-label">{{ t('objects.wizard.basicInfo.registrationNumber') }}</label>
        <!-- <small class="field-hint">{{ t('objects.wizard.basicInfo.registrationNumberHint') }}</small> -->
        <input
          pInputText
          id="registrationNumber"
          formControlName="registrationNumber"
          data-testid="wizard-basic-registration-number"
          [fluid]="true"
          [placeholder]="t('objects.wizard.basicInfo.registrationNumberPlaceholder')"
        />
        @if (getErrorMessage('registrationNumber'); as error) {
          <small class="field-error">{{ error }}</small>
        }
      </div>

      <!-- Description -->
      <div class="form-group">
        <label class="group-label">{{ t('objects.wizard.basicInfo.description') }}</label>
        <p-editor
          #descriptionEditor
          data-testid="wizard-basic-description"
          formControlName="description"
          [style]="{ height: '200px' }"
          [readonly]="isViewMode()"
          [placeholder]="t('objects.wizard.basicInfo.descriptionPlaceholder')"
          [modules]="editorModules"
        >
          <ng-template pTemplate="header">
            <span class="ql-formats">
              <select class="ql-header">
                <option value="1">H1</option>
                <option value="2">H2</option>
                <option value="3">H3</option>
                <option selected>Normal</option>
              </select>
            </span>
            <span class="ql-formats">
              <button type="button" class="ql-bold"></button>
              <button type="button" class="ql-italic"></button>
              <button type="button" class="ql-underline"></button>
            </span>
            <span class="ql-formats">
              <select class="ql-color"></select>
            </span>
            <span class="ql-formats">
              <button type="button" class="ql-align" value=""></button>
              <button type="button" class="ql-align" value="center"></button>
              <button type="button" class="ql-align" value="right"></button>
            </span>
            <span class="ql-formats">
              <button type="button" class="ql-list" value="bullet"></button>
            </span>
            <span class="ql-formats">
              <button type="button" class="ql-emoji"></button>
            </span>
          </ng-template>
        </p-editor>
        @if (getErrorMessage('description'); as error) {
          <small class="field-error">{{ error }}</small>
        }
      </div>

      <!-- Address Section -->
      <div class="form-group" formGroupName="address" data-testid="wizard-basic-address-section">
        <label class="group-label">{{ t('objects.wizard.basicInfo.address') }}</label>

        <ng-template #suggestionItemTpl let-item>
          <div class="address-suggestion">
            <div class="address-suggestion__main">
              <span class="address-suggestion__name">{{ getSuggestionPrimary(item) }}</span>
              @if (getSuggestionBadge(item); as badge) {
                <span class="address-suggestion__badge">{{ badge }}</span>
              }
            </div>
            @if (getSuggestionSecondary(item); as secondary) {
              <small class="address-suggestion__hint">{{ secondary }}</small>
            }
          </div>
        </ng-template>

        <div class="form-row">
          <div class="form-group flex-2">
            <label class="field-label">{{ t('objects.wizard.basicInfo.city') }}</label>
            <p-autoComplete
              inputId="city"
              formControlName="city"
              data-testid="wizard-basic-city"
              autocomplete="new-password"
              [suggestions]="citySuggestions()"
              optionLabel="formatted"
              [delay]="0"
              [minQueryLength]="3"
              [fluid]="true"
              [readonly]="isViewMode()"
              [placeholder]="t('objects.wizard.basicInfo.cityPlaceholder')"
              (completeMethod)="onCitySearch($event)"
              (onSelect)="onCitySelect($event)"
            >
              <ng-template let-item pTemplate="item">
                <ng-container *ngTemplateOutlet="suggestionItemTpl; context: { $implicit: item }" />
              </ng-template>
            </p-autoComplete>
            @if (getErrorMessage('address.city'); as error) {
              <small class="field-error">{{ error }}</small>
            }
          </div>

          <div class="form-group flex-1">
            <label class="field-label">{{ t('objects.wizard.basicInfo.postalCode') }}</label>
            <input
              pInputText
              id="postalCode"
              formControlName="postalCode"
              data-testid="wizard-basic-postal-code"
              [fluid]="true"
              [placeholder]="t('objects.wizard.basicInfo.postalCodePlaceholder')"
            />
            @if (getErrorMessage('address.postalCode'); as error) {
              <small class="field-error">{{ error }}</small>
            }
          </div>
        </div>

        <div class="form-row no-street-row">
          <p-checkbox
            inputId="noStreet"
            formControlName="noStreet"
            [binary]="true"
            data-testid="wizard-basic-no-street"
            (onChange)="onNoStreetToggle($event.checked)"
          />
          <label for="noStreet" class="no-street-label">
            {{ t('objects.wizard.basicInfo.noStreet') }}
          </label>
        </div>

        <div class="form-row">
          <div class="form-group flex-2">
            <label class="field-label">{{ t('objects.wizard.basicInfo.street') }}</label>
            <p-autoComplete
              inputId="street"
              formControlName="street"
              data-testid="wizard-basic-street"
              autocomplete="new-password"
              [suggestions]="streetSuggestions()"
              optionLabel="formatted"
              [delay]="0"
              [minQueryLength]="2"
              [fluid]="true"
              [readonly]="streetReadonly() || isViewMode()"
              [placeholder]="t('objects.wizard.basicInfo.streetPlaceholder')"
              (completeMethod)="onStreetSearch($event)"
              (onSelect)="onStreetSelect($event)"
            >
              <ng-template let-item pTemplate="item">
                <ng-container *ngTemplateOutlet="suggestionItemTpl; context: { $implicit: item }" />
              </ng-template>
            </p-autoComplete>
            @if (getErrorMessage('address.street'); as error) {
              <small class="field-error">{{ error }}</small>
            }
          </div>

          <div class="form-group flex-1">
            <label class="field-label">{{ t('objects.wizard.basicInfo.streetNumber') }}</label>
            <input
              pInputText
              id="streetNumber"
              formControlName="streetNumber"
              data-testid="wizard-basic-street-number"
              [fluid]="true"
              [readonly]="streetNumberReadonly() || isViewMode()"
              [placeholder]="t('objects.wizard.basicInfo.streetNumberPlaceholder')"
              (blur)="onStreetNumberBlur()"
            />
            @if (getErrorMessage('address.streetNumber'); as error) {
              <small class="field-error">{{ error }}</small>
            }
          </div>
        </div>

        <!-- Hidden country field -->
        <input type="hidden" formControlName="country" />

        <!-- Map -->
        <div class="map-container">
          <label class="field-label">{{ t('objects.wizard.basicInfo.mapLocation') }}</label>
          <small class="field-hint">{{ t('objects.wizard.basicInfo.mapHint') }}</small>
          <ngx-map-picker
            #mapPicker
            [config]="mapConfig"
            [addressFormGroup]="addressFormGroup()"
            [addressFieldsConfig]="addressFieldsConfig"
            [autoGeocodeFromFields]="false"
            [disabled]="isViewMode()"
            [readonly]="isViewMode()"
            (mapReady)="onMapReady()"
            (locationChange)="onLocationChange($event)"
          />
        </div>
      </div>

      <!-- Amenities -->
      <div class="form-group">
        <label class="group-label">{{ t('objects.wizard.basicInfo.amenities') }}</label>
        @if (amenitiesLoading()) {
          <small class="field-hint">{{ t('objects.wizard.basicInfo.amenitiesLoading') }}</small>
        } @else {
          <app-amenity-selector
            data-testid="wizard-basic-amenities"
            label=""
            [amenities]="propertyAmenities()"
            [disabled]="isViewMode()"
            [ngModel]="form.get('amenityIds')?.value"
            [ngModelOptions]="{ standalone: true }"
            (selectionChange)="onAmenitiesChange($event)"
          />
        }
      </div>

      <!-- Contact Visibility -->
      <div class="form-group">
        <label class="group-label">{{ t('objects.wizard.contactVisibility.title') }}</label>
        <small class="field-hint">{{ t('objects.wizard.contactVisibility.hint') }}</small>

        <div class="contact-toggles" data-testid="wizard-basic-contact-toggles">
          <div class="contact-toggle-row">
            <div class="toggle-row">
              <p-toggleswitch
                data-testid="wizard-basic-show-phone"
                [ngModel]="form.get('showPhone')?.value"
                [ngModelOptions]="{ standalone: true }"
                (onChange)="onContactToggleChange('showPhone', $event.checked)"
                inputId="showPhone"
              />
              <label for="showPhone" class="toggle-label">
                <i class="pi pi-phone"></i>
                {{ t('objects.wizard.contactVisibility.showPhone') }}
              </label>
            </div>
            @if (form.get('showPhone')?.value) {
              <div class="contact-input">
                <input
                  pInputText
                  formControlName="phone"
                  data-testid="wizard-basic-phone"
                  [fluid]="true"
                  [placeholder]="t('objects.wizard.contactVisibility.phonePlaceholder')"
                />
                @if (getErrorMessage('phone'); as error) {
                  <small class="field-error">{{ error }}</small>
                }
              </div>
            }
          </div>

          <div class="contact-toggle-row">
            <div class="toggle-row">
              <p-toggleswitch
                data-testid="wizard-basic-show-whatsapp"
                [ngModel]="form.get('showWhatsApp')?.value"
                [ngModelOptions]="{ standalone: true }"
                (onChange)="onContactToggleChange('showWhatsApp', $event.checked)"
                inputId="showWhatsApp"
              />
              <label for="showWhatsApp" class="toggle-label">
                <i class="pi pi-whatsapp"></i>
                {{ t('objects.wizard.contactVisibility.showWhatsApp') }}
              </label>
            </div>
            @if (form.get('showWhatsApp')?.value) {
              <div class="contact-input">
                <input
                  pInputText
                  formControlName="whatsApp"
                  data-testid="wizard-basic-whatsapp"
                  [fluid]="true"
                  [placeholder]="t('objects.wizard.contactVisibility.whatsAppPlaceholder')"
                />
                @if (getErrorMessage('whatsApp'); as error) {
                  <small class="field-error">{{ error }}</small>
                }
              </div>
            }
          </div>

          <div class="contact-toggle-row">
            <div class="toggle-row">
              <p-toggleswitch
                data-testid="wizard-basic-show-email"
                [ngModel]="form.get('showEmail')?.value"
                [ngModelOptions]="{ standalone: true }"
                (onChange)="onContactToggleChange('showEmail', $event.checked)"
                inputId="showEmail"
              />
              <label for="showEmail" class="toggle-label">
                <i class="pi pi-envelope"></i>
                {{ t('objects.wizard.contactVisibility.showEmail') }}
              </label>
            </div>
            @if (form.get('showEmail')?.value) {
              <div class="contact-input">
                <input
                  pInputText
                  formControlName="email"
                  data-testid="wizard-basic-email"
                  [fluid]="true"
                  [placeholder]="t('objects.wizard.contactVisibility.emailPlaceholder')"
                />
                @if (getErrorMessage('email'); as error) {
                  <small class="field-error">{{ error }}</small>
                }
              </div>
            }
          </div>
        </div>

        <!-- preferredContact is always 'form' (hidden from user) -->
      </div>
    </form>

    @if (!isViewMode()) {
      <ng-template #footer>
        @if (submitted() && missingFields().length > 0) {
          <div class="validation-summary">
            <p-message severity="warn">
              <span class="validation-summary-content">
                <strong>{{ t('objects.wizard.validation.missingFieldsTitle') }}</strong>
                <ul class="validation-summary-list">
                  @for (field of missingFields(); track field) {
                    <li>{{ field }}</li>
                  }
                </ul>
              </span>
            </p-message>
          </div>
        }
        <div class="card-footer">
          <p-button data-testid="wizard-basic-cancel-btn" [label]="t('common.cancel')" severity="secondary" [text]="true" (onClick)="onCancel()" />
          <p-button
            data-testid="wizard-basic-next-btn"
            [label]="t('objects.wizard.footer.next')"
            icon="pi pi-chevron-right"
            iconPos="right"
            (onClick)="onNext()"
          />
        </div>
      </ng-template>
    }
  </p-card>
</div>
